/**
 * A prototype as one HTML document, pure: every screen a `<section data-screen>` the size of its
 * frame, every element placed in it as real HTML (a button is a `<button>`, an input an `<input>`
 * to type in, a checkbox ticks, a list is a list), with the same look as on the board
 * (`shared/elements.ts`) and its way out (`data-go`, `data-transition`). Pictures are written into
 * it (`data:` addresses), so it needs nothing else.
 *
 * Without `site`, it holds no script: the Prototype window plays it from the app (`player.ts`).
 * With `site`, it is the exported prototype: the player is written in, a bar gives Back and
 * Restart, and a strict CSP lets it load nothing from anywhere. Everything typed on the board is
 * escaped, and style values become CSS only once checked (`lookOf`).
 */
import { cssText, frameLook, lookOf, pixels, showsWords, type Css } from "../../shared/elements";
import { playPrototype } from "./player";
import type { ProtoElement, Prototype } from "./screens";

/** Text written into HTML: `&`, `<`, `>`, `"` and `'` escaped. */
export function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}

/** The document's own styles: the elements' base, as the board draws them. */
const BASE_CSS = `*{box-sizing:border-box}html,body{margin:0;height:100%}
body{background:#2a2833;font-family:system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;display:flex;flex-direction:column;align-items:center;justify-content:center;overflow:hidden}
#device{position:relative;flex:none;transform-origin:center center;box-shadow:0 12px 48px rgb(0 0 0/.4);border-radius:14px;overflow:hidden;background:#fff}
.screen{position:absolute;inset:0;overflow-x:hidden;overflow-y:auto;background:#fff}
.screen[hidden]{display:none}
.content{position:relative}
.el{position:absolute;margin:0;line-height:1.25;color:#1f1d2b;overflow:hidden;overflow-wrap:anywhere}
.box{border:1px solid transparent}
.text{padding:2px 4px;white-space:pre-wrap;display:flex;flex-direction:column;justify-content:flex-start}
.note{padding:10px 12px;white-space:pre-wrap;box-shadow:0 2px 6px rgb(0 0 0/.18);border-radius:2px;display:flex;flex-direction:column;justify-content:center}
.shape,.card{padding:10px 12px;border:2px solid transparent;display:flex;flex-direction:column;justify-content:center;white-space:pre-wrap}
.card{border:1px solid #d9d6e4;border-radius:8px}
.shape-rounded{border-radius:18px}.shape-ellipse{border-radius:50%}.shape-diamond{clip-path:polygon(50% 0,100% 50%,50% 100%,0 50%)}.shape-triangle{clip-path:polygon(50% 0,100% 100%,0 100%)}
img.image{display:block;width:100%;height:100%}
.missing{display:flex;align-items:center;justify-content:center;background:#ecebf2;color:#6b6880;font-size:13px}
.button{display:flex;align-items:center;padding:0 14px;font-family:inherit;cursor:pointer;white-space:nowrap;text-overflow:ellipsis}
.button:active{filter:brightness(.92)}
.input{padding:0 12px;font-family:inherit;outline:none}
.input:focus{box-shadow:0 0 0 3px rgb(106 75 224/.25)}
.checkbox{display:flex;align-items:center;gap:8px;cursor:pointer}
.checkbox input{width:18px;height:18px;margin:0;flex:none;accent-color:#6a4be0}
.list{list-style:none;padding:0;overflow-y:auto}
.list li{padding:8px 12px;border-bottom:1px solid #ecebf2}
.instance{border:1px solid transparent}
[data-go]{cursor:pointer}
#bar{display:flex;gap:12px;align-items:center;margin-top:14px;color:#e8e6f0;font-size:14px}
#bar button{font:inherit;background:#3c3947;color:inherit;border:0;border-radius:6px;padding:6px 12px;cursor:pointer}
#bar button:disabled{opacity:.4;cursor:default}`;

/** Where an element sits in what holds it. */
function placed(e: ProtoElement): Css {
  return { left: `${e.x}px`, top: `${e.y}px`, width: `${e.w}px`, height: `${e.h}px`, ...(e.rotate ? { transform: `rotate(${e.rotate}deg)` } : {}) };
}

/** The attributes of an element's way out. */
function way(e: ProtoElement): string {
  return e.go ? ` data-go="${escapeHtml(e.go)}" data-transition="${escapeHtml(e.transition)}"` : "";
}

/** Words as HTML, their lines kept. */
function words(text: string): string {
  return escapeHtml(text);
}

/** One element as HTML. `pictures`: a project file's `data:` address. */
function elementHtml(e: ProtoElement, pictures: ReadonlyMap<string, string>): string {
  const id = ` data-element="${escapeHtml(e.id)}"`;
  if (e.shape === "box") return `<div class="el box"${id} style="${escapeHtml(cssText({ ...placed(e), ...frameLook(e.style) }))}"></div>`;
  const look = escapeHtml(cssText({ ...placed(e), ...lookOf(e.shape, e.style) }));
  const attrs = `${id} style="${look}"${way(e)}`;
  switch (e.shape) {
    case "image": {
      const src = e.picture ? pictures.get(e.picture) : undefined;
      return src && /^data:image\//.test(src) ? `<img class="el image"${attrs} src="${escapeHtml(src)}" alt="${escapeHtml(e.text)}">` : `<div class="el image missing"${attrs}>${words(e.text)}</div>`;
    }
    case "button":
      return `<button type="button" class="el button"${attrs}>${words(e.text)}</button>`;
    case "input":
      return `<input class="el input"${attrs} type="${e.style.secret === "yes" ? "password" : "text"}" placeholder="${escapeHtml(e.text)}" aria-label="${escapeHtml(e.text)}">`;
    case "checkbox":
      return `<label class="el checkbox"${attrs}><input type="checkbox"${e.style.checked === "yes" ? " checked" : ""}><span>${words(e.text)}</span></label>`;
    case "list":
      return `<ul class="el list"${attrs}>${e.text
        .split("\n")
        .filter((line) => line.trim())
        .map((line) => `<li>${words(line)}</li>`)
        .join("")}</ul>`;
    case "instance": {
      const frame = escapeHtml(cssText({ ...placed(e), ...frameLook(e.frame ?? {}) }));
      return `<div class="el instance"${id} style="${frame}"${way(e)}>${e.parts.map((p) => elementHtml(p, pictures)).join("")}</div>`;
    }
    case "shape": {
      const shape = /^(rounded|ellipse|diamond|triangle)$/.test(e.style.shape ?? "") ? ` shape-${e.style.shape}` : "";
      return `<div class="el shape${shape}"${attrs}>${showsWords(e.shape, e.kind) ? words(e.text) : ""}</div>`;
    }
    default:
      return `<div class="el ${e.shape}"${attrs}>${words(e.text)}</div>`;
  }
}

/** What {@link prototypeHtml} needs besides the prototype. */
export interface HtmlOptions {
  /** The document's title. */
  title: string;
  /** Pictures by project file: `data:` addresses (others are left out). */
  pictures: ReadonlyMap<string, string>;
  /** The exported site: the player written in, a bar, a strict CSP. */
  site?: boolean;
  /** The screen played first (none: the prototype's own start). */
  start?: string | null;
}

/** The prototype as one HTML document. */
export function prototypeHtml(prototype: Prototype, options: HtmlOptions): string {
  const screens = prototype.screens
    .map((s) => {
      const content = escapeHtml(cssText({ width: `${s.w}px`, height: `${s.h}px`, ...frameLook(s.style) }));
      const elements = s.elements.map((e) => elementHtml(e, options.pictures)).join("");
      return `<section class="screen" data-screen="${escapeHtml(s.id)}" data-name="${escapeHtml(s.name)}" data-w="${pixels(String(s.viewport.w), 390, 1, 10000)}" data-h="${pixels(String(s.viewport.h), 844, 1, 10000)}" hidden><div class="content" style="${content}">${elements}</div></section>`;
    })
    .join("\n");
  const start = options.start ?? prototype.start;
  const site = options.site
    ? `<nav id="bar"><button type="button" id="bar-back">‹ Back</button><span id="bar-name"></span><button type="button" id="bar-restart">Restart</button></nav>
<script>(${playPrototype.toString()})(document, { start: ${JSON.stringify(start).replace(/</g, "\\u003c")} });</script>`
    : "";
  const csp = options.site ? `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data:; style-src 'unsafe-inline'; script-src 'unsafe-inline'">\n` : "";
  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8">
${csp}<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(options.title)}</title>
<style>${BASE_CSS}</style></head>
<body><div id="device">
${screens}
</div>
${site}</body></html>
`;
}
